import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, Mail, Lock, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { cognitoAuth } from '@/services/cognitoAuth';
import { logger, ValidationError, NetworkError } from '@/utils/logger';

interface AdminLoginProps {
  onLoginSuccess: () => void;
}

type AuthMode = 'signin' | 'signup' | 'confirm';

interface AuthState {
  loading: boolean;
  error: string | null;
  mode: AuthMode;
}

export const AdminLogin: React.FC<AdminLoginProps> = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authState, setAuthState] = useState<AuthState>({
    loading: false,
    error: null,
    mode: 'signin'
  });

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthState({ loading: true, error: null, mode: 'signin' });

    try {
      await cognitoAuth.signIn(email, password);
      logger.info('Admin login successful');
      onLoginSuccess();
    } catch (error) {
      logger.error('Admin login failed:', error);
      let errorMessage = 'Login failed';
      if (error instanceof ValidationError) {
        errorMessage = error.message;
        if (error.message.includes('confirm')) {
          setAuthState({ loading: false, error: errorMessage, mode: 'confirm' });
          return;
        }
      } else if (error instanceof NetworkError) {
        errorMessage = error.message;
      }
      setAuthState({ loading: false, error: errorMessage, mode: 'signin' });
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthState({ loading: true, error: null, mode: 'signup' });

    try {
      const result = await cognitoAuth.signUp(email, password, name, companyName);
      logger.info('Admin signup successful:', { userId: result.userId });
      
      if (result.confirmationRequired) {
        setAuthState({ loading: false, error: null, mode: 'confirm' });
      } else {
        // Auto sign-in if no confirmation required
        await cognitoAuth.signIn(email, password);
        onLoginSuccess();
      }
    } catch (error) {
      logger.error('Admin signup failed:', error);
      let errorMessage = 'Signup failed';
      if (error instanceof ValidationError) {
        errorMessage = error.message;
      } else if (error instanceof NetworkError) {
        errorMessage = error.message;
      }
      setAuthState({ loading: false, error: errorMessage, mode: 'signup' });
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthState({ loading: true, error: null, mode: 'confirm' });

    try {
      await cognitoAuth.confirmSignUp(email, confirmationCode);
      logger.info('Email confirmation successful');
      
      // Auto sign-in after confirmation
      await cognitoAuth.signIn(email, password);
      onLoginSuccess();
    } catch (error) {
      logger.error('Email confirmation failed:', error);
      let errorMessage = 'Confirmation failed';
      if (error instanceof ValidationError) {
        errorMessage = error.message;
      } else if (error instanceof NetworkError) {
        errorMessage = error.message;
      }
      setAuthState({ loading: false, error: errorMessage, mode: 'confirm' });
    }
  };

  const switchMode = (mode: AuthMode) => {
    setAuthState(prev => ({ ...prev, mode, error: null }));
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-900 text-white mb-4">
            <Shield className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Admin Portal</h1>
          <p className="text-slate-600 mt-1">CollectABILITY Internal Access</p>
        </div>

        {/* Form Container */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 ">
          {/* Mode Tabs */}
          {authState.mode !== 'confirm' && (
            <div className="flex border-b border-slate-200 mb-6">
              <button
                onClick={() => switchMode('signin')}
                className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
                  authState.mode === 'signin'
                    ? 'text-slate-900 border-b-2 border-slate-900'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => switchMode('signup')}
                className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
                  authState.mode === 'signup'
                    ? 'text-slate-900 border-b-2 border-slate-900'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Sign Up
              </button>
            </div>
          )}

          {/* Error Message */}
          {authState.error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <AlertTriangle className="h-4 w-4 text-red-500 mr-2" />
                <span className="text-sm text-red-700">{authState.error}</span>
              </div>
            </div>
          )}

          {/* Sign In Form */}
          {authState.mode === 'signin' && (
            <form onSubmit={handleSignIn} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@company.com"
                    required
                    className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    disabled={authState.loading}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="w-full pl-9 pr-10 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    disabled={authState.loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={authState.loading}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white py-2 px-4 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {authState.loading ? 'Signing In...' : 'Sign In'}
              </button>
            </form>
          )}

          {/* Sign Up Form */}
          {authState.mode === 'signup' && (
            <form onSubmit={handleSignUp} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Smith"
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                  disabled={authState.loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Your Company"
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                  disabled={authState.loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@company.com"
                    required
                    className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    disabled={authState.loading}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="w-full pl-9 pr-10 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    disabled={authState.loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Must be at least 8 characters with uppercase, lowercase, number, and special character
                </p>
              </div>

              <button
                type="submit"
                disabled={authState.loading}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white py-2 px-4 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {authState.loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>
          )}

          {/* Email Confirmation Form */}
          {authState.mode === 'confirm' && (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <h3 className="text-lg font-medium text-slate-900 mb-2">Confirm Your Email</h3>
                <p className="text-sm text-slate-600">
                  We've sent a confirmation code to <strong>{email}</strong>
                </p>
              </div>

              <form onSubmit={handleConfirm} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Confirmation Code
                  </label>
                  <input
                    type="text"
                    value={confirmationCode}
                    onChange={(e) => setConfirmationCode(e.target.value)}
                    placeholder="123456"
                    required
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent text-center text-lg tracking-widest"
                    disabled={authState.loading}
                    maxLength={6}
                  />
                </div>

                <button
                  type="submit"
                  disabled={authState.loading || confirmationCode.length < 6}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white py-2 px-4 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {authState.loading ? 'Confirming...' : 'Confirm Email'}
                </button>

                <button
                  type="button"
                  onClick={() => switchMode('signin')}
                  className="w-full text-slate-600 hover:text-slate-800 py-2 text-sm transition-colors"
                >
                  Back to Sign In
                </button>
              </form>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};
